import { mdiPlus, mdiLoading } from '@mdi/js';
import Icon from '@mdi/react';
import Link from 'next/link';

import * as styles from '@/styles/components/Button';

type StyleProps = {
  size?: 'md' | 'lg';
  variant?:
    | 'primary'
    | 'destructive'
    | 'outlined'
    | 'primary-outline'
    | 'destructive-outline'
    | 'link'
    | 'link-primary'
    | 'text';
  disabled?: boolean;
  active?: boolean;
  icon?: boolean;
  borderColor?: string;
  color?: string;
  backgroundColor?: string;
  padding?: string;
  width?: string;
};

type ButtonProps = {
  type: 'button' | 'submit';
  href?: undefined;
  target?: undefined;
  handleClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  ariaLabel?: string;
} & JSX.IntrinsicElements['button'] &
  StyleProps;

type LinkProps = {
  type?: undefined;
  href: string;
  target?: undefined;
  handleClick?: undefined;
  ariaLabel?: string;
} & StyleProps;

type ExternalLinkProps = {
  type?: undefined;
  href: string;
  target: '_blank';
  handleClick?: undefined;
  ariaLabel?: string;
} & JSX.IntrinsicElements['a'] &
  StyleProps;

const PlusIcon = ({ size = 'md' }: { size: 'md' | 'lg' }) => {
  const iconSize = size === 'md' ? '2rem' : '2.4rem';

  return (
    <span css={styles.icon}>
      <Icon path={mdiPlus} title='' size={iconSize} />
    </span>
  );
};

const LoadingIcon = ({ size = 'md' }: { size: 'md' | 'lg' }) => {
  const iconSize = size === 'md' ? '2rem' : '2.4rem';

  return (
    <span css={styles.icon}>
      <Icon path={mdiLoading} title='' size={iconSize} spin={0.6} />
    </span>
  );
};

const Button: React.FC<ButtonProps | LinkProps | ExternalLinkProps> = (props) => {
  const size = props.size || 'md';
  const variant = props.variant || 'primary';
  const disabled = props.disabled || false;
  const active = props.active || false;
  const icon = props.icon || false;

  // 外部リンク (aタグ)
  if (props.href && props.target === '_blank') {
    return (
      <a
        css={styles.root(props.color)}
        href={props.href}
        target='_blank'
        data-size={size}
        data-variant={variant}
        data-active={active}
        aria-disabled={disabled}
        rel='noreferrer'>
        <span css={styles.inner}>
          {active && <LoadingIcon size={size} />}
          {icon && <PlusIcon size={size} />}
          {props.children}
        </span>
      </a>
    );
  }

  // 内部リンク (Linkコンポーネント)
  if (props.href && !props.target) {
    return (
      <Link href={props.href} passHref>
        <a
          css={styles.root(props.color)}
          data-size={size}
          data-variant={variant}
          data-active={active}
          aria-disabled={disabled}>
          <span css={styles.inner}>
            {active && <LoadingIcon size={size} />}
            {icon && <PlusIcon size={size} />}
            {props.children}
          </span>
        </a>
      </Link>
    );
  }

  // buttonタグ
  return (
    <button
      css={styles.root(props.color, props.borderColor, props.backgroundColor, props.padding, props.width)}
      type={props.type}
      data-size={size}
      data-variant={variant}
      data-active={active}
      aria-disabled={disabled}
      onClick={props.handleClick}
      aria-label={props.ariaLabel}>
      <span css={styles.inner}>
        {active && <LoadingIcon size={size} />}
        {icon && <PlusIcon size={size} />}
        {props.children}
      </span>
    </button>
  );
};

export default Button;
